import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StopSessionInput } from '@gc/shared';

import { onDigitsChange } from '@/lib/digits';

/**
 * The rounds a ROUND-billed device's End asks for — Round Type and Rounds —
 * and the stop body they make.
 *
 * Born in the Device List's End (`EndReservationDialog`) and shared from here
 * so Outstanding's End asks exactly the same thing (owner ruling OW19,
 * 2026-11-03). A pool table or a console sold by the match is priced on
 * rounds alone (`computeSessionAmount` ignores the clock for those), and the
 * stop schema reads a missing count as zero, so an End that does not ask
 * closes the sitting owing nothing for play. Hourly devices have nothing to
 * count; their End posts `{}`.
 */

export type StopRoundType = 'SINGLE' | 'DOUBLE' | 'THREE_BALL';

/** What staff picked and typed, as typed. */
export interface StopRoundsEntry {
  roundType: StopRoundType;
  rounds: string;
}

/** One single round — never zero: zero is a free table nobody chose. */
export const STOP_ROUNDS_DEFAULT: StopRoundsEntry = { roundType: 'SINGLE', rounds: '1' };

/**
 * Whole rounds, never fewer than none, and finite: "Infinity" or "1e999" read
 * as Infinity, which JSON sends as null and the server refuses. Read as none
 * instead, so the "nothing to pay" line says so (W2 review, round 1).
 */
export function stopRoundCount(rounds: string): number {
  const typed = Number(rounds);
  return Number.isFinite(typed) ? Math.max(0, Math.floor(typed)) : 0;
}

/**
 * The round type that will be billed. A 3-ball pick where there is no 3-ball
 * price (the board polls under an open dialog, and a price can go) falls back
 * to what the select now shows: the server bills `(threeBallPriceMd ?? 0) ×
 * rounds`, so 3 ball without a price ended those games free.
 */
export function stopRoundType(entry: StopRoundsEntry, offersThreeBall: boolean): StopRoundType {
  return entry.roundType === 'THREE_BALL' && !offersThreeBall ? 'SINGLE' : entry.roundType;
}

/** The stop body for a ROUND-billed device: every count present, one of them set. */
export function stopRoundsPayload(
  entry: StopRoundsEntry,
  offersThreeBall: boolean,
): StopSessionInput {
  const roundType = stopRoundType(entry, offersThreeBall);
  const count = stopRoundCount(entry.rounds);
  return {
    singleRounds: roundType === 'SINGLE' ? count : 0,
    doubleRounds: roundType === 'DOUBLE' ? count : 0,
    threeBallRounds: roundType === 'THREE_BALL' ? count : 0,
  };
}

const FIELD_CLASS =
  'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary';

/**
 * Round Type beside Rounds, and the "nothing to pay" line when none are
 * counted. 3 ball is offered only where there is a 3-ball price — the till's
 * own round picker has the same rule (TabCheckoutDrawer).
 */
export function StopRoundsFields({
  entry,
  offersThreeBall,
  onChange,
  autoFocus = false,
}: {
  entry: StopRoundsEntry;
  offersThreeBall: boolean;
  onChange: (next: StopRoundsEntry) => void;
  autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const roundType = stopRoundType(entry, offersThreeBall);
  const roundCount = stopRoundCount(entry.rounds);

  return (
    <>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="mb-1 block text-xs text-content-secondary">
            {t('sessions.roundType')}
          </span>
          <select
            value={roundType}
            onChange={(event) =>
              onChange({ ...entry, roundType: event.target.value as StopRoundType })
            }
            className={FIELD_CLASS}
          >
            <option value="SINGLE">{t('sessions.single')}</option>
            <option value="DOUBLE">{t('sessions.double')}</option>
            {offersThreeBall ? <option value="THREE_BALL">{t('sessions.threeBall')}</option> : null}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-xs text-content-secondary">
            {t('sessions.roundsPlayed')}
          </span>
          {/* Text with the numeric keypad, not type="number" (rulings L4): a
              number field reads "٣" from an Arabic keypad as "", and "" is
              zero rounds — a table billed nothing. Normalised as typed, so it
              reads 3. */}
          <input
            type="text"
            inputMode="numeric"
            value={entry.rounds}
            onChange={onDigitsChange((rounds) => onChange({ ...entry, rounds }))}
            autoFocus={autoFocus}
            className={`${FIELD_CLASS} tabular`}
          />
        </label>
      </div>

      {roundCount === 0 ? (
        // Legal — a table someone sat at and never played on — but it charges
        // nothing, so it must be a choice rather than a default nobody noticed.
        <p className="mt-3 text-xs text-ending">{t('deviceBoard.endDialog.noRounds')}</p>
      ) : null}
    </>
  );
}

/** One round-priced sitting a bulk stop must count, under its device's name. */
export interface StopRoundsLine {
  sessionId: string;
  deviceName: string;
  offersThreeBall: boolean;
}

/**
 * The rounds a bulk stop asks — one block per round-priced sitting — for
 * Outstanding's End and the Device List's bulk Pause alike, inside
 * `BulkConfirmDialog`. Unanswered lines read as `STOP_ROUNDS_DEFAULT`.
 *
 * The cursor opens in the first line's Rounds, where the Device List's End
 * opens it (OW19 review): staff used to typing the count there would
 * otherwise fill the dialog's typed count, arm it, and bill the default one
 * round whatever was played. The dialog must then not take the cursor back
 * (`autoFocusTypedCount={false}`). Picked once, when the dialog opens, so a
 * line the poll adds later never pulls the cursor out of a field mid-count.
 */
export function StopRoundsLines({
  lines,
  rounds,
  onChange,
}: {
  lines: readonly StopRoundsLine[];
  rounds: ReadonlyMap<string, StopRoundsEntry>;
  onChange: (sessionId: string, next: StopRoundsEntry) => void;
}) {
  const [focusedAtOpen] = useState(() => lines[0]?.sessionId ?? null);
  if (lines.length === 0) return null;

  return (
    <div className="mt-3 max-h-72 space-y-3 overflow-auto">
      {lines.map((line) => (
        <fieldset
          key={line.sessionId}
          data-end-rounds-for={line.sessionId}
          className="rounded-control border border-subtle px-3 pb-3"
        >
          <legend className="px-1 text-xs font-semibold text-content">{line.deviceName}</legend>
          <StopRoundsFields
            entry={rounds.get(line.sessionId) ?? STOP_ROUNDS_DEFAULT}
            offersThreeBall={line.offersThreeBall}
            onChange={(next) => onChange(line.sessionId, next)}
            autoFocus={line.sessionId === focusedAtOpen}
          />
        </fieldset>
      ))}
    </div>
  );
}
